/**
 * Flag pickup, chain scoring, powerups, extra-life awards.
 *
 *  - The Nth flag (1-indexed in the chain) is worth N * baseFlagScore.
 *  - state.scoreMultiplier (1 by default, 2 after a doubler flag) multiplies
 *    every flag's score. Resets at startStage; persists across deaths in the
 *    same stage.
 *  - The doubler flag itself scores at the pre-doubled rate; the multiplier
 *    flips to 2 AFTER the doubler is collected, so only subsequent flags
 *    benefit.
 *  - The fuel_bonus flag adds floor(fuel/60) * fuelBonusPerSecond on top of
 *    its chain value.
 *  - The speed_boost flag sets speedBoostUntil; collecting it again extends
 *    the timer (max(current, tick+duration)) rather than stacking.
 *  - Extra lives at extraLifeFirst, then every extraLifeStep.
 */

import type { FlagState, GameEvent, GameState } from './types.ts';

const YELLOW_FLAGS_PER_STAGE = 10;

/** Compute the chain-and-multiplier score for `flag`. Does not include the
 * fuel-bonus add-on, which collectFlag layers on top. */
export function flagValueFor(state: GameState, flag: FlagState): number {
  void flag;
  return (state.chainStep + 1) * state.config.baseFlagScore * state.scoreMultiplier;
}

/** Pure: collect the flag at `flagIndex` (must exist and not yet be collected). */
export function collectFlag(state: GameState, flagIndex: number): GameState {
  const flag = state.flags[flagIndex];
  if (!flag) throw new Error(`collectFlag: invalid index ${flagIndex}`);
  if (flag.collected) throw new Error(`collectFlag: flag ${flagIndex} already collected`);

  let value = flagValueFor(state, flag);
  if (flag.kind === 'fuel_bonus') {
    value += Math.floor(state.fuel / 60) * state.config.fuelBonusPerSecond;
  }

  const yellowCollected =
    flag.kind === 'yellow' ? state.yellowCollected + 1 : state.yellowCollected;

  const flags = state.flags.map((f, i) =>
    i === flagIndex ? { ...f, collected: true } : f,
  );

  const newScore = state.score + value;
  const events: GameEvent[] = [
    ...state.events,
    { kind: 'flag_pickup', score: value, flagKind: flag.kind },
  ];

  // Doubler flips multiplier AFTER scoring so it only affects subsequent flags.
  const scoreMultiplier = flag.kind === 'doubler' ? 2 : state.scoreMultiplier;

  const speedBoostUntil =
    flag.kind === 'speed_boost'
      ? Math.max(state.speedBoostUntil, state.tick + state.config.speedBoostDuration)
      : state.speedBoostUntil;

  // Extra-life award. Cross a threshold => +1 life, advance threshold.
  let lives = state.lives;
  let nextExtraLifeAt = state.nextExtraLifeAt;
  while (newScore >= nextExtraLifeAt) {
    lives += 1;
    events.push({ kind: 'extra_life' });
    nextExtraLifeAt += state.config.extraLifeStep;
  }

  const stageCleared = yellowCollected >= YELLOW_FLAGS_PER_STAGE;
  if (stageCleared) events.push({ kind: 'stage_clear' });

  return {
    ...state,
    flags,
    score: newScore,
    chainStep: state.chainStep + 1,
    scoreMultiplier,
    speedBoostUntil,
    yellowCollected,
    lives,
    nextExtraLifeAt,
    phase: stageCleared ? 'cleared' : state.phase,
    events,
  };
}
